import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Vault.css';
import { renderVaultImportExportModal } from './vault/renderVaultImportExportModal';
import { useVaultExport } from './vault/useVaultExport';
import { useVaultImport } from './vault/useVaultImport';

export default function VaultImportExportModal({ isOpen, mode = 'import', onClose }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const masterRef = useRef(null);

  // Import state
  const [sourceKey, setSourceKey] = useState('bitwardenJson');
  const [fileName, setFileName] = useState('');
  // { items, skipped, passkeysFound? } | null — passkeysFound is Bitwarden JSON
  // only; every other parser leaves it undefined and the line stays off.
  const [parsed, setParsed] = useState(null);
  // { content } | null — an encrypted export waiting for its file password.
  // Mutually exclusive with `parsed`: items exist only after Rust unseals.
  const [sealed, setSealed] = useState(null);
  const [sealedPassword, setSealedPassword] = useState('');
  const [unsealing, setUnsealing] = useState(false);
  const [skipDupes, setSkipDupes] = useState(true);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Export state
  const [exportFormat, setExportFormat] = useState('encrypted');
  const [exportPassword, setExportPassword] = useState('');
  const [exportPasswordConfirm, setExportPasswordConfirm] = useState('');
  const [plainAck, setPlainAck] = useState(false);
  const [masterSecret, setMasterSecret] = useState('');
  const [exporting, setExporting] = useState(false);

  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setSourceKey('bitwardenJson');
    setFileName('');
    setParsed(null);
    setSealed(null);
    setSealedPassword('');
    setUnsealing(false);
    setSkipDupes(true);
    setImporting(false);
    setProgress({ done: 0, total: 0 });
    setExportFormat('encrypted');
    setExportPassword('');
    setExportPasswordConfirm('');
    setPlainAck(false);
    setMasterSecret('');
    setExporting(false);
    setError(null);
  }, [isOpen]);

  const {
    changeSource, typeCounts, pickFile, onFileInputChange, runUnseal, runImport,
  } = useVaultImport({
    t, sealed, onClose, fileInputRef, sourceKey, setSourceKey, fileName, setFileName, parsed,
    setParsed, setSealed, sealedPassword, setSealedPassword, unsealing, setUnsealing, skipDupes,
    importing, setImporting, setProgress, setError,
  });

  const {
    exportPasswordMismatch, canExport, CSV_EXPORTS, changeExportFormat, runExport, onExportKeyDown,
  } = useVaultExport({
    t, onClose, masterRef, exportFormat, setExportFormat, exportPassword, exportPasswordConfirm,
    plainAck, setPlainAck, masterSecret, exporting, setExporting, setError,
  });

  if (!isOpen) return null;

  return renderVaultImportExportModal({
    t, sealed, mode, onClose, fileInputRef, masterRef, sourceKey, fileName, parsed, sealedPassword,
    setSealedPassword, unsealing, skipDupes, setSkipDupes, importing, progress, exportFormat,
    exportPassword, setExportPassword, exportPasswordConfirm, setExportPasswordConfirm, plainAck,
    setPlainAck, masterSecret, setMasterSecret, exporting, error, setError, changeSource,
    typeCounts, pickFile, onFileInputChange, runUnseal, runImport, exportPasswordMismatch,
    runExport, canExport, CSV_EXPORTS, changeExportFormat, onExportKeyDown,
  });
}
